import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketingShell } from "@/components/site/marketing-shell";
import { Shield, Lock, Database, Cookie, UserCheck, Mail, AlertTriangle, Server } from "lucide-react";

export const Route = createFileRoute("/trust")({
  head: () => ({
    meta: [
      { title: "Trust & Security — CryptoBull Pro" },
      { name: "description", content: "How CryptoBull Pro handles account security, data, cookies, and privacy requests." },
      { property: "og:title", content: "Trust & Security — CryptoBull Pro" },
      { property: "og:description", content: "Security, privacy, and data practices for the CryptoBull Pro app." },
    ],
  }),
  component: Trust,
});

const sections = [
  {
    icon: UserCheck,
    title: "Access & authentication",
    body: "Accounts are protected with email and password sign-in backed by our authentication provider. Sessions use signed bearer tokens that are validated on every protected server call. Administrative actions are gated by a server-side role check — no user can elevate their own privileges from the browser.",
  },
  {
    icon: Shield,
    title: "Financial integrity",
    body: "Balances, deposits, withdrawals, investments, and referral earnings cannot be modified directly from the client. Every money-moving action runs through a trusted server-side function that validates the request, locks the affected row, and writes all related records in a single atomic transaction. Deposit and withdrawal approvals are admin-only and cannot be double-applied.",
  },
  {
    icon: Lock,
    title: "Data isolation",
    body: "Row-level security is enabled on every application table. By default a signed-in user can only read and modify their own records; administrative reads and writes require an explicit admin role. Support ticket messages flagged as official Support replies can only be posted by admins.",
  },
  {
    icon: Server,
    title: "Platform & hosting",
    body: "The app runs on Lovable Cloud, which provides the managed database, authentication, and serverless runtime. Traffic to the app is served over HTTPS. Lovable Cloud provides infrastructure capabilities; how those capabilities are used inside this app is the responsibility of the app owner. This page is not a certification or independent audit.",
  },
  {
    icon: Database,
    title: "Data we collect",
    body: "We store the information you provide to operate your account: email, optional display name, optional BTC payout address, plan selections, deposit and withdrawal records, and support ticket history. We do not sell personal data. Specific retention periods and data-processing terms are set by the app owner — contact us using the address below for details.",
  },
  {
    icon: Cookie,
    title: "Cookies & analytics",
    body: "We use first-party storage to keep you signed in and to remember UI preferences. We do not currently load third-party advertising trackers. If analytics or marketing tools are added in the future, this page will be updated.",
  },
  {
    icon: AlertTriangle,
    title: "Vulnerability reporting",
    body: "If you believe you have found a security issue, please email the security contact below with steps to reproduce. Please do not publicly disclose the issue until we have had a reasonable opportunity to respond.",
  },
  {
    icon: Mail,
    title: "Security & privacy contact",
    body: "For privacy requests (access, correction, deletion) or security reports, open a ticket from the Support page after signing in, or contact the app owner using the channel listed in the site footer.",
  },
];

function Trust() {
  return (
    <MarketingShell>
      <section className="mx-auto max-w-4xl px-4 py-20">
        <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
          <Shield className="h-4 w-4 text-primary" /> Trust & Security
        </div>
        <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tight">How we protect your account</h1>
        <p className="mt-6 text-lg text-muted-foreground">
          This page is maintained by the CryptoBull Pro team to answer common security and privacy questions about the app. It describes practices and controls that are currently in place — it is editable project content, not an independent certification or third-party audit.
        </p>

        <div className="mt-12 space-y-4">
          {sections.map(({ icon: Ic, title, body }) => (
            <div key={title} className="glass rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="rounded-xl bg-primary/10 p-2 text-primary">
                  <Ic className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-semibold">{title}</h2>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-white/10 bg-background/40 p-6 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">Shared responsibility.</strong> Lovable Cloud provides the underlying platform (managed database, authentication, and serverless runtime). The CryptoBull Pro team is responsible for application logic, access policies, and customer communications. You are responsible for protecting your account credentials and for the accuracy of the wallet addresses you provide.
          </p>
          <p className="mt-3">
            Have a question that is not answered here?{" "}
            <Link to="/support" className="text-primary hover:underline">Contact support</Link>.
          </p>
        </div>
      </section>
    </MarketingShell>
  );
}